import { useState } from "react";
import { Link } from "react-router-dom";
import Layout, { Breadcrumb } from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_URL = "https://readdy.ai/api/form/da2j87bd3r93mn1k39u0";

const contactWays = [
  { icon: "ri-mail-line", title: "邮箱", desc: "contact@comicapp-ranking.cn" },
  { icon: "ri-wechat-line", title: "微信公众号", desc: "漫选社" },
  { icon: "ri-time-line", title: "回复时间", desc: "工作日 9:00 - 18:00" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "联系我们 - 漫选社",
    description:
      "联系漫选社：无论是漫画软件应用推荐、内容合作还是使用反馈，都欢迎通过表单与我们沟通。",
    canonical: "https://mangao0.github.io/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们",
      description: "联系漫选社的表单与联系方式",
      url: "https://mangao0.github.io/contact",
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setStatus("submitting");

    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("website_alt") || "").toString().trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(msg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试");
    }
  };

  return (
    <Layout title="联系我们">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          <div className="rounded-3xl border border-background-200/80 bg-background-100 p-6 md:p-8">
            <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h1>
            <p className="mt-2 text-sm text-foreground-600">
              无论是漫画软件应用推荐、内容合作还是使用反馈，都欢迎通过下方表单与我们沟通。
            </p>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-3 rounded-2xl bg-accent-100/70 p-4 text-sm text-accent-900" role="status">
                <i className="ri-checkbox-circle-fill mt-0.5 text-lg" aria-hidden="true"></i>
                提交成功！感谢你的来信，我们会尽快与你联系。
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-start gap-3 rounded-2xl bg-primary-100 p-4 text-sm text-primary-800" role="alert">
                <i className="ri-error-warning-fill mt-0.5 text-lg" aria-hidden="true"></i>
                {formError || "提交失败，请稍后再试。"}
              </div>
            )}

            <form id="contact-form" data-readdy-form="联系漫选社表单" onSubmit={onSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label htmlFor="name" className="mb-1.5 text-sm font-bold text-foreground-950">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={50}
                    placeholder="如何称呼你"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="email" className="mb-1.5 text-sm font-bold text-foreground-950">邮箱地址</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="you@example.com"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label htmlFor="subject" className="mb-1.5 text-sm font-bold text-foreground-950">主题</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  maxLength={80}
                  placeholder="想聊些什么"
                  className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col">
                <label htmlFor="message" className="mb-1.5 text-sm font-bold text-foreground-950">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="写下你的想法、建议或合作意向（最多 500 字）"
                  className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
                <span className="mt-1 text-right text-xs text-foreground-400">最多 500 字</span>
              </div>

              <div className="hp-field" aria-hidden="true">
                <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-8 py-3 text-base font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "正在提交…" : "提交留言"}
                <i className="ri-send-plane-line" aria-hidden="true"></i>
              </button>
            </form>
          </div>

          <aside className="space-y-5">
            <div className="rounded-3xl border border-background-200/80 bg-background-100 p-6">
              <h2 className="text-base font-bold text-foreground-950">其他联系方式</h2>
              <ul className="mt-4 space-y-4">
                {contactWays.map((w) => (
                  <li key={w.title} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-900">
                      <i className={`${w.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{w.title}</p>
                      <p className="mt-0.5 text-sm text-foreground-600">{w.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-accent-100/70 p-6">
              <h2 className="text-base font-bold text-foreground-950">还没想好要问什么？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                先看看常见问题或完整排行榜，也许答案就在里面。
              </p>
              <div className="mt-4 flex flex-col gap-2.5">
                <Link to="/faq" className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                  查看常见问题
                </Link>
                <Link to="/rankings" className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg border-2 border-primary-500 px-5 py-2.5 text-sm font-bold text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50">
                  查看排行榜
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}